import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiEdit2, FiTrash2, FiPlus, FiStar, FiCheck, FiX } from 'react-icons/fi';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ reach_out_number: '' });
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [designSettings, setDesignSettings] = useState({
    primary_color: '#FFB800',
    secondary_color: '#FF6B9E',
    background_color: '#FFFDF7',
    text_color: '#3A1C00',
    hero_image: '',
    logo_text: 'Mango Cosmetics',
    tagline: 'Natural lip care with a fruity twist'
  });
  const [activeTab, setActiveTab] = useState('products');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [productForm, setProductForm] = useState({
    name: '',
    price: '',
    image: '',
    description: '',
    ingredients: '',
    benefits: '',
    how_to_use: '',
    stock_status: 'In Stock',
    category_id: '',
    status: 'draft',
    variants: []
  });
  const [variantForm, setVariantForm] = useState({ name: '', price: '' });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const [productsRes, settingsRes, reviewsRes, designRes, categoriesRes, mediaRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/all/admin`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/all`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design-settings`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const productsData = await productsRes.json();
      const settingsData = await settingsRes.json();
      const reviewsData = await reviewsRes.json();
      const designData = await designRes.json();
      const categoriesData = await categoriesRes.json();
      const mediaData = await mediaRes.json();
      
      setProducts(productsData);
      setSettings(settingsData);
      setReviews(reviewsData);
      setDesignSettings(designData);
      setCategories(categoriesData);
      setMediaFiles(mediaData);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const url = editingProduct
        ? `${process.env.REACT_APP_BACKEND_URL}/api/products/${editingProduct.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...productForm,
          price: parseFloat(productForm.price)
        })
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Product updated!' : 'Product created!');
        setShowProductForm(false);
        setEditingProduct(null);
        resetProductForm();
        loadData();
      } else {
        toast.error('Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Product deleted!');
        loadData();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      price: product.price.toString(),
      image: product.image,
      description: product.description,
      ingredients: product.ingredients,
      benefits: product.benefits,
      how_to_use: product.how_to_use,
      stock_status: product.stock_status,
      category_id: product.category_id || '',
      status: product.status || 'draft',
      variants: product.variants || []
    });
    setShowProductForm(true);
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings updated!');
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleUpdateDesignSettings = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/design-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(designSettings)
      });

      if (response.ok) {
        toast.success('Design settings updated! Refresh to see changes.');
      } else {
        toast.error('Failed to update design settings');
      }
    } catch (error) {
      console.error('Error updating design settings:', error);
      toast.error('Failed to update design settings');
    }
  };

  const addVariant = () => {
    if (variantForm.name && variantForm.price) {
      setProductForm({
        ...productForm,
        variants: [...productForm.variants, { name: variantForm.name, price: parseFloat(variantForm.price) }]
      });
      setVariantForm({ name: '', price: '' });
    }
  };

  const removeVariant = (index) => {
    setProductForm({
      ...productForm,
      variants: productForm.variants.filter((_, i) => i !== index)
    });
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      price: '',
      image: '',
      description: '',
      ingredients: '',
      benefits: '',
      how_to_use: '',
      stock_status: 'In Stock',
      category_id: '',
      status: 'draft',
      variants: []
    });
  };

  const handleCreateCategory = async (name) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, description: '' })
      });

      if (response.ok) {
        toast.success('Category created!');
        loadData();
      } else {
        toast.error('Failed to create category');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to create category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure? Products in this category will be uncategorized.')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Category deleted!');
        loadData();
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleUploadMedia = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('admin_token');
    
    // If single file, use single upload
    if (files.length === 1) {
      const formData = new FormData();
      formData.append('file', files[0]);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(`Image uploaded! Saved ${data.compression_ratio} space`);
          loadData();
          e.target.value = '';
        } else {
          toast.error('Failed to upload image');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to upload image');
      }
    } else {
      // Bulk upload
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload/bulk`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          toast.success(`${data.uploaded} images uploaded! ${data.failed} failed.`);
          loadData();
          e.target.value = '';
        } else {
          toast.error('Failed to upload images');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to upload images');
      }
    }
  };

  const handleDeleteMedia = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Image deleted!');
        loadData();
      } else {
        toast.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete image');
    }
  };

  const copyImageUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Image URL copied to clipboard!');
  };

  const handleApproveReview = async (reviewId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Review approved!');
        loadData();
      } else {
        toast.error('Failed to approve review');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to approve review');
    }
  };

  const handleRejectReview = async (reviewId) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}/reject`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Review rejected!');
        loadData();
      } else {
        toast.error('Failed to reject review');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to reject review');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Review deleted!');
        loadData();
      } else {
        toast.error('Failed to delete review');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete review');
    }
  };

  const handleAddReply = async (reviewId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reply_text: replyText })
      });

      if (response.ok) {
        toast.success('Reply added!');
        setReplyingTo(null);
        setReplyText('');
        loadData();
      } else {
        toast.error('Failed to add reply');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to add reply');
    }
  };

  const handleDeleteReply = async (reviewId, replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reviews/${reviewId}/reply/${replyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Reply deleted!');
        loadData();
      } else {
        toast.error('Failed to delete reply');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete reply');
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        size={16}
        fill={i < rating ? '#FFB800' : 'none'}
        stroke={i < rating ? '#FFB800' : '#FFEAB3'}
      />
    ));
  };

  const pendingReviews = reviews.filter(r => r.status === 'pending');
  const approvedReviews = reviews.filter(r => r.status === 'approved');
  const rejectedReviews = reviews.filter(r => r.status === 'rejected');

  return (
    <div className="content-wrapper py-12 px-4 sm:px-6 lg:px-8 min-h-screen bg-[#F9F6F0]" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl font-black font-['Fredoka'] text-[#3A1C00] mb-8" data-testid="admin-dashboard-title">
            Admin Dashboard
          </h1>

          {/* Tabs */}
          <div className="flex gap-4 mb-8">
            <button
              onClick={() => setActiveTab('products')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'products'
                  ? 'bg-[#FFB800] text-[#3A1C00]'
                  : 'bg-white text-[#7D5A35] hover:bg-[#FFF1E5]'
              }`}
              data-testid="products-tab"
            >
              Products
            </button>
            <button
              onClick={() => setActiveTab('media')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'media'
                  ? 'bg-[#FFB800] text-[#3A1C00]'
                  : 'bg-white text-[#7D5A35] hover:bg-[#FFF1E5]'
              }`}
              data-testid="media-tab"
            >
              Media Library
            </button>
            <button
              onClick={() => setActiveTab('design')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'design'
                  ? 'bg-[#FFB800] text-[#3A1C00]'
                  : 'bg-white text-[#7D5A35] hover:bg-[#FFF1E5]'
              }`}
              data-testid="design-tab"
            >
              Design
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`px-6 py-3 rounded-full font-bold transition-all ${
                activeTab === 'reviews'
                  ? 'bg-[#FFB800] text-[#3A1C00]'
                  : 'bg-white text-[#7D5A35] hover:bg-[#FFF1E5]'
              }`}
              data-testid="reviews-tab"
            >
              Reviews {pendingReviews.length > 0 && `(${pendingReviews.length})`}
            </button>
          </div>

          {/* Settings Section */}
          {activeTab === 'products' && (
            <>
              {/* Category Management */}
              <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm" data-testid="categories-section">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">Product Categories</h2>
                <div className="flex gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="New category name (e.g., Lip Balms)"
                    className="admin-input flex-1"
                    id="newCategoryInput"
                    data-testid="new-category-input"
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('newCategoryInput');
                      if (input.value.trim()) {
                        handleCreateCategory(input.value.trim());
                        input.value = '';
                      }
                    }}
                    className="btn-primary"
                    data-testid="add-category-button"
                  >
                    Add Category
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  {categories.map((category, index) => (
                    <div
                      key={category.id}
                      className="bg-[#FFF1E5] px-4 py-2 rounded-full flex items-center gap-2"
                      data-testid={`category-${index}`}
                    >
                      <span className="font-bold text-[#3A1C00]">{category.name}</span>
                      <button
                        onClick={() => handleDeleteCategory(category.id)}
                        className="text-[#FF3B30] hover:text-[#FF1F13]"
                        data-testid={`delete-category-${index}`}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm" data-testid="settings-section">
            <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">Contact Settings</h2>
            <form onSubmit={handleUpdateSettings} className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-[#3A1C00] font-['Fredoka'] font-bold mb-2" htmlFor="reachOut">
                  Reach Out Number
                </label>
                <input
                  type="text"
                  id="reachOut"
                  value={settings.reach_out_number}
                  onChange={(e) => setSettings({ reach_out_number: e.target.value })}
                  className="admin-input"
                  placeholder="+1-800-MANGO"
                  data-testid="reach-out-number-input"
                />
              </div>
              <button type="submit" className="btn-primary" data-testid="update-settings-button">
                Update
              </button>
            </form>
          </div>

          {/* Products Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="products-section">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00]">Products</h2>
              <button
                onClick={() => {
                  setShowProductForm(true);
                  setEditingProduct(null);
                  resetProductForm();
                }}
                className="btn-primary flex items-center gap-2"
                data-testid="add-product-button"
              >
                <FiPlus size={20} />
                Add Product
              </button>
            </div>

            {showProductForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-[#F9F6F0] rounded-2xl p-6 mb-6"
                data-testid="product-form"
              >
                <h3 className="text-xl font-bold font-['Fredoka'] text-[#3A1C00] mb-4">
                  {editingProduct ? 'Edit Product' : 'New Product'}
                </h3>
                <form onSubmit={handleProductSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#3A1C00] font-bold mb-2">Product Name</label>
                      <input
                        type="text"
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        className="admin-input"
                        required
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-[#3A1C00] font-bold mb-2">Price</label>
                      <input
                        type="number"
                        step="0.01"
                        value={productForm.price}
                        onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                        className="admin-input"
                        required
                        data-testid="product-price-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Product Image</label>
                    <div className="space-y-3">
                      <input
                        type="url"
                        value={productForm.image}
                        onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                        className="admin-input"
                        placeholder="Paste image URL or upload below"
                        required
                        data-testid="product-image-input"
                      />
                      <div className="flex gap-3 items-center">
                        <span className="text-[#7D5A35] text-sm">Or upload:</span>
                        <label className="btn-secondary text-sm py-2 cursor-pointer">
                          Choose File
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const formData = new FormData();
                                formData.append('file', file);
                                const token = localStorage.getItem('admin_token');
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/media/upload`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  if (response.ok) {
                                    const data = await response.json();
                                    setProductForm({ ...productForm, image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` });
                                    toast.success('Image uploaded!');
                                  }
                                } catch (error) {
                                  toast.error('Failed to upload');
                                }
                                e.target.value = '';
                              }
                            }}
                            className="hidden"
                            data-testid="product-image-upload"
                          />
                        </label>
                      </div>
                      {productForm.image && (
                        <img src={productForm.image} alt="Preview" className="w-32 h-32 object-cover rounded-xl" />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Description</label>
                    <textarea
                      value={productForm.description}
                      onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                      className="admin-input"
                      rows="3"
                      required
                      data-testid="product-description-input"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Ingredients</label>
                    <textarea
                      value={productForm.ingredients}
                      onChange={(e) => setProductForm({ ...productForm, ingredients: e.target.value })}
                      className="admin-input"
                      rows="2"
                      required
                      data-testid="product-ingredients-input"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Benefits</label>
                    <textarea
                      value={productForm.benefits}
                      onChange={(e) => setProductForm({ ...productForm, benefits: e.target.value })}
                      className="admin-input"
                      rows="2"
                      required
                      data-testid="product-benefits-input"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">How to Use</label>
                    <textarea
                      value={productForm.how_to_use}
                      onChange={(e) => setProductForm({ ...productForm, how_to_use: e.target.value })}
                      className="admin-input"
                      rows="2"
                      required
                      data-testid="product-how-to-use-input"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Stock Status</label>
                    <select
                      value={productForm.stock_status}
                      onChange={(e) => setProductForm({ ...productForm, stock_status: e.target.value })}
                      className="admin-input"
                      data-testid="product-stock-status-select"
                    >
                      <option value="In Stock">In Stock</option>
                      <option value="Low Stock">Low Stock</option>
                      <option value="Out of Stock">Out of Stock</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Category</label>
                    <select
                      value={productForm.category_id}
                      onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                      className="admin-input"
                      data-testid="product-category-select"
                    >
                      <option value="">Uncategorized</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Status</label>
                    <select
                      value={productForm.status}
                      onChange={(e) => setProductForm({ ...productForm, status: e.target.value })}
                      className="admin-input"
                      data-testid="product-status-select"
                    >
                      <option value="draft">Draft (Hidden from customers)</option>
                      <option value="published">Published (Visible to customers)</option>
                    </select>
                    <p className="text-sm text-[#7D5A35] mt-2">
                      {productForm.status === 'draft' ? '⚠️ This product will NOT be visible to customers' : '✅ This product will be visible to customers'}
                    </p>
                  </div>

                  {/* Variants */}
                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Variants</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={variantForm.name}
                        onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                        className="admin-input flex-1"
                        placeholder="Variant name"
                        data-testid="variant-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.price}
                        onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                        className="admin-input w-32"
                        placeholder="Price"
                        data-testid="variant-price-input"
                      />
                      <button
                        type="button"
                        onClick={addVariant}
                        className="btn-secondary"
                        data-testid="add-variant-button"
                      >
                        Add
                      </button>
                    </div>
                    <div className="space-y-2">
                      {productForm.variants.map((variant, index) => (
                        <div key={index} className="flex justify-between items-center bg-white p-3 rounded-xl" data-testid={`variant-item-${index}`}>
                          <span>{variant.name} - ${variant.price.toFixed(2)}</span>
                          <button
                            type="button"
                            onClick={() => removeVariant(index)}
                            className="text-[#FF3B30] hover:text-[#FF1F13]"
                            data-testid={`remove-variant-${index}`}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button type="submit" className="btn-primary" data-testid="save-product-button">
                      {editingProduct ? 'Update Product' : 'Create Product'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProductForm(false);
                        setEditingProduct(null);
                        resetProductForm();
                      }}
                      className="btn-secondary"
                      data-testid="cancel-product-button"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* Products List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-list">
              {products.map((product, index) => (
                <div key={product.id} className="bg-[#F9F6F0] rounded-2xl p-4" data-testid={`admin-product-${index}`}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover rounded-xl mb-3"
                    data-testid={`admin-product-image-${index}`}
                  />
                  <div className="mb-2 flex gap-2 flex-wrap">
                    {product.status === 'draft' && (
                      <span className="bg-[#FF9500] text-white px-2 py-1 rounded-full text-xs font-bold" data-testid={`product-draft-badge-${index}`}>
                        DRAFT
                      </span>
                    )}
                    {product.category_name && (
                      <span className="bg-[#FF6B9E] text-white px-2 py-1 rounded-full text-xs font-bold">
                        {product.category_name}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold font-['Fredoka'] text-[#3A1C00] mb-1" data-testid={`admin-product-name-${index}`}>
                    {product.name}
                  </h3>
                  <p className="text-xl font-bold text-[#FFB800] mb-3" data-testid={`admin-product-price-${index}`}>
                    ${product.price.toFixed(2)}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="flex-1 bg-[#FFB800] text-[#3A1C00] font-bold py-2 px-4 rounded-full hover:bg-[#FFA000] transition-all flex items-center justify-center gap-2"
                      data-testid={`edit-product-${index}`}
                    >
                      <FiEdit2 size={16} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.id)}
                      className="bg-[#FF3B30] text-white font-bold py-2 px-4 rounded-full hover:bg-[#FF1F13] transition-all"
                      data-testid={`delete-product-${index}`}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
            </>
          )}

          {/* Media Library Tab */}
          {activeTab === 'media' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="media-library-section">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00]">Media Library</h2>
                <label className="btn-primary cursor-pointer" data-testid="upload-media-button">
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleUploadMedia}
                    className="hidden"
                    data-testid="media-file-input"
                  />
                </label>
              </div>

              <p className="text-[#7D5A35] mb-6">Upload and manage your product images. Click on an image URL to copy it.</p>

              {mediaFiles.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-[#7D5A35] mb-4">No images uploaded yet</p>
                  <label className="btn-secondary cursor-pointer inline-block">
                    Upload Your First Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleUploadMedia}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {mediaFiles.map((media, index) => (
                    <div 
                      key={media.id} 
                      className="bg-[#F9F6F0] rounded-2xl p-4 group relative"
                      data-testid={`media-item-${index}`}
                    >
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${media.url}`}
                        alt={media.original_filename}
                        className="w-full h-48 object-cover rounded-xl mb-3"
                        data-testid={`media-image-${index}`}
                      />
                      <div className="space-y-2">
                        <p className="text-xs text-[#7D5A35] truncate" title={media.original_filename}>
                          {media.original_filename}
                        </p>
                        <p className="text-xs text-[#7D5A35]">
                          {(media.size / 1024).toFixed(1)} KB
                        </p>
                        <button
                          onClick={() => copyImageUrl(media.url)}
                          className="w-full bg-[#FFB800] text-[#3A1C00] text-sm font-bold py-2 px-3 rounded-full hover:bg-[#FFA000] transition-all"
                          data-testid={`copy-url-${index}`}
                        >
                          Copy URL
                        </button>
                        <button
                          onClick={() => handleDeleteMedia(media.id)}
                          className="w-full bg-[#FF3B30] text-white text-sm font-bold py-2 px-3 rounded-full hover:bg-[#FF1F13] transition-all"
                          data-testid={`delete-media-${index}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Design Settings Tab */}
          {activeTab === 'design' && (
            <div className="bg-white rounded-2xl p-6 shadow-sm" data-testid="design-settings-section">
              <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">Customize Site Design</h2>
              <p className="text-[#7D5A35] mb-8">Customize your site's appearance. Changes will apply after page refresh.</p>
              
              <form onSubmit={handleUpdateDesignSettings} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Primary Color (Buttons, Accents)</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={designSettings.primary_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, primary_color: e.target.value })}
                        className="w-16 h-12 rounded-lg cursor-pointer"
                        data-testid="primary-color-picker"
                      />
                      <input
                        type="text"
                        value={designSettings.primary_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, primary_color: e.target.value })}
                        className="admin-input flex-1"
                        placeholder="#FFB800"
                        data-testid="primary-color-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Secondary Color (Badges, Highlights)</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={designSettings.secondary_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, secondary_color: e.target.value })}
                        className="w-16 h-12 rounded-lg cursor-pointer"
                        data-testid="secondary-color-picker"
                      />
                      <input
                        type="text"
                        value={designSettings.secondary_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, secondary_color: e.target.value })}
                        className="admin-input flex-1"
                        placeholder="#FF6B9E"
                        data-testid="secondary-color-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Background Color</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={designSettings.background_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, background_color: e.target.value })}
                        className="w-16 h-12 rounded-lg cursor-pointer"
                        data-testid="background-color-picker"
                      />
                      <input
                        type="text"
                        value={designSettings.background_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, background_color: e.target.value })}
                        className="admin-input flex-1"
                        placeholder="#FFFDF7"
                        data-testid="background-color-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-bold mb-2">Text Color</label>
                    <div className="flex gap-3">
                      <input
                        type="color"
                        value={designSettings.text_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, text_color: e.target.value })}
                        className="w-16 h-12 rounded-lg cursor-pointer"
                        data-testid="text-color-picker"
                      />
                      <input
                        type="text"
                        value={designSettings.text_color}
                        onChange={(e) => setDesignSettings({ ...designSettings, text_color: e.target.value })}
                        className="admin-input flex-1"
                        placeholder="#3A1C00"
                        data-testid="text-color-input"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-[#3A1C00] font-bold mb-2">Logo Text</label>
                  <input
                    type="text"
                    value={designSettings.logo_text}
                    onChange={(e) => setDesignSettings({ ...designSettings, logo_text: e.target.value })}
                    className="admin-input"
                    placeholder="Mango Cosmetics"
                    data-testid="logo-text-input"
                  />
                </div>

                <div>
                  <label className="block text-[#3A1C00] font-bold mb-2">Tagline</label>
                  <input
                    type="text"
                    value={designSettings.tagline}
                    onChange={(e) => setDesignSettings({ ...designSettings, tagline: e.target.value })}
                    className="admin-input"
                    placeholder="Natural lip care with a fruity twist"
                    data-testid="tagline-input"
                  />
                </div>

                <div>
                  <label className="block text-[#3A1C00] font-bold mb-2">Hero Image URL</label>
                  <input
                    type="url"
                    value={designSettings.hero_image}
                    onChange={(e) => setDesignSettings({ ...designSettings, hero_image: e.target.value })}
                    className="admin-input"
                    placeholder="https://images.unsplash.com/..."
                    data-testid="hero-image-input"
                  />
                  {designSettings.hero_image && (
                    <img 
                      src={designSettings.hero_image} 
                      alt="Hero preview" 
                      className="mt-4 w-full max-w-md h-48 object-cover rounded-xl"
                    />
                  )}
                </div>

                <button type="submit" className="btn-primary" data-testid="save-design-button">
                  Save Design Settings
                </button>
              </form>
            </div>
          )}

          {/* Reviews Section */}
          {activeTab === 'reviews' && (
            <div className="space-y-8">
              {/* Pending Reviews */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">
                  Pending Reviews ({pendingReviews.length})
                </h2>
                {pendingReviews.length === 0 ? (
                  <p className="text-[#7D5A35]">No pending reviews</p>
                ) : (
                  <div className="space-y-4">
                    {pendingReviews.map((review, index) => (
                      <div key={review.id} className="bg-[#F9F6F0] rounded-2xl p-6" data-testid={`pending-review-${index}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-bold font-['Fredoka'] text-[#3A1C00]">{review.customer_name}</h3>
                            <p className="text-sm text-[#7D5A35]">{review.customer_email}</p>
                            <div className="flex gap-1 mt-2">{renderStars(review.rating)}</div>
                          </div>
                          <span className="text-sm text-[#7D5A35]">
                            {new Date(review.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-[#7D5A35] mb-4">{review.review_text}</p>
                        {review.image_url && (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${review.image_url}`}
                            alt="Review"
                            className="w-32 h-32 object-cover rounded-xl mb-4"
                          />
                        )}
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApproveReview(review.id)}
                            className="flex items-center gap-2 bg-[#34C759] text-white font-bold py-2 px-4 rounded-full hover:bg-[#2FB84D] transition-all"
                            data-testid={`approve-review-${index}`}
                          >
                            <FiCheck size={16} />
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectReview(review.id)}
                            className="flex items-center gap-2 bg-[#FF3B30] text-white font-bold py-2 px-4 rounded-full hover:bg-[#FF1F13] transition-all"
                            data-testid={`reject-review-${index}`}
                          >
                            <FiX size={16} />
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Approved Reviews */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">
                  Approved Reviews ({approvedReviews.length})
                </h2>
                {approvedReviews.length === 0 ? (
                  <p className="text-[#7D5A35]">No approved reviews</p>
                ) : (
                  <div className="space-y-4">
                    {approvedReviews.map((review, index) => (
                      <div key={review.id} className="bg-[#F9F6F0] rounded-2xl p-6" data-testid={`approved-review-${index}`}>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-lg font-bold font-['Fredoka'] text-[#3A1C00]">{review.customer_name}</h3>
                            <div className="flex gap-1 mt-2">{renderStars(review.rating)}</div>
                          </div>
                          <button
                            onClick={() => handleDeleteReview(review.id)}
                            className="text-[#FF3B30] hover:text-[#FF1F13]"
                            data-testid={`delete-approved-review-${index}`}
                          >
                            <FiTrash2 size={20} />
                          </button>
                        </div>
                        <p className="text-[#7D5A35] mb-4">{review.review_text}</p>
                        {review.image_url && (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${review.image_url}`}
                            alt="Review"
                            className="w-32 h-32 object-cover rounded-xl mb-4"
                          />
                        )}
                        
                        {/* Existing Replies */}
                        {review.replies && review.replies.length > 0 && (
                          <div className="ml-6 mb-4 space-y-3">
                            {review.replies.map((reply, replyIndex) => (
                              <div 
                                key={reply.id} 
                                className="bg-white rounded-xl p-4 border-l-4 border-[#FFB800]"
                                data-testid={`admin-reply-${index}-${replyIndex}`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <span className="bg-[#FFB800] text-[#3A1C00] px-2 py-1 rounded-full text-xs font-bold">
                                    Your Reply
                                  </span>
                                  <button
                                    onClick={() => handleDeleteReply(review.id, reply.id)}
                                    className="text-[#FF3B30] hover:text-[#FF1F13]"
                                  >
                                    <FiTrash2 size={14} />
                                  </button>
                                </div>
                                <p className="text-[#3A1C00] text-sm">{reply.reply_text}</p>
                                <span className="text-xs text-[#7D5A35]">
                                  {new Date(reply.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Reply Form */}
                        {replyingTo === review.id ? (
                          <div className="mt-4 space-y-3">
                            <textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              className="admin-input"
                              rows="3"
                              placeholder="Write your reply..."
                              data-testid={`reply-textarea-${index}`}
                            ></textarea>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAddReply(review.id)}
                                className="btn-primary text-sm py-2"
                                data-testid={`submit-reply-${index}`}
                              >
                                Post Reply
                              </button>
                              <button
                                onClick={() => {
                                  setReplyingTo(null);
                                  setReplyText('');
                                }}
                                className="btn-secondary text-sm py-2"
                                data-testid={`cancel-reply-${index}`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setReplyingTo(review.id)}
                            className="mt-4 text-[#FFB800] font-bold hover:text-[#FF9E00] transition-colors"
                            data-testid={`reply-button-${index}`}
                          >
                            Reply to Customer
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDashboard;